import { Text } from '@/components'
import { NavigationPage } from '@/app/components'
import { AdaptiveCardRowLayout } from '@/app/layouts'
import { useParams } from 'react-router-dom'
import PageLoader from '@/authenticated/components/layout/PageLoader'
import { Thermostat, ThermostatComfortSetting } from '@/types'
import { thermostatComfortSettingsCollection } from '@/reducers/thermostatComfortSettings'
import { thermostatsCollection } from '@/reducers/thermostats'
import { useBrandedThermostatName } from '@/hooks/useBrandedThermostatName'
import EventSettingsRow from '@/app/features/devices/thermostat/EventSettingsRow'

const PRECOOL_LEARN_MORE_URL =
  'https://optiwatt.zendesk.com/hc/en-us/articles/41017692178331-How-does-Optiwatt-s-Precool-feature-work'

type ViewModelProps = {
  thermostatId: Thermostat['id']
  comfortSettings: ThermostatComfortSetting[]
  comfortSettingsError: boolean
  isNest: boolean
}

function EventTemperaturesPageComponent(
  props: ViewModelProps & { brandedThermostatName: string | undefined },
) {
  // Nest has no schedule, so we describe a single relative shift; other brands
  // shift each scheduled comfort setting, so we ask for a target per schedule.
  const introCopy = props.isNest
    ? "We'll shift your temperature from what your Nest is set at when the event starts. For example, +3 for a cooling event means we'll shift your temperature from 72° to 75° when the event starts."
    : 'What should we shift your temperature to during an event? Choose a target for each of your schedules.'

  return (
    <NavigationPage
      id="thermostat-event-temperatures-page"
      title="Event Temperatures"
      subtitle={props.brandedThermostatName ?? 'Thermostat'}
      navigationOptions={['back']}
      headerClassName="mb-3"
    >
      <AdaptiveCardRowLayout.PlainRow className="mt-0 mb-0">
        <Text variant="body2">{introCopy}</Text>
      </AdaptiveCardRowLayout.PlainRow>
      {/*
        Reuses the same event-temperature steppers shown on the old grid-support
        page. EventSettingsRow forks by brand: Nest -> single relative delta,
        Ecobee/Honeywell -> absolute setpoints per comfort setting. Editing is
        always enabled here (the grid-support on/off toggle now lives inline on
        the "Be a Watt Hero" card on the Programs tab), and the intro copy is
        rendered above rather than inside the editor.
      */}
      <EventSettingsRow
        editingEnabled
        showInstructions={false}
        comfortSettingsError={props.comfortSettingsError}
        thermostatId={props.thermostatId}
        comfortSettings={props.comfortSettings}
      />
      <AdaptiveCardRowLayout.CardRow bottomRule={false}>
        <div className="flex flex-col gap-2">
          <Text variant="subheader">Pre-cool before events</Text>
          <Text variant="body2">
            Before a cooling event, we&apos;ll cool your home ahead of time so
            you stay comfortable during the temperature shift. How long we
            pre-cool for and to what temperature varies based on the outdoor
            temperature and how well-insulated your home is.{' '}
            <a
              href={PRECOOL_LEARN_MORE_URL}
              target="_blank"
              rel="noreferrer"
              className="font-bold text-base-blue-500"
            >
              Learn more
            </a>
          </Text>
        </div>
      </AdaptiveCardRowLayout.CardRow>
    </NavigationPage>
  )
}

function EventTemperaturesPage(props: ViewModelProps) {
  const brandedThermostatName = useBrandedThermostatName(props.thermostatId)
  return (
    <EventTemperaturesPageComponent
      {...props}
      brandedThermostatName={brandedThermostatName}
    />
  )
}

function EventTemperaturesPageWithFallback(props: {
  thermostatId: Thermostat['id']
}) {
  const {
    data: thermostats,
    status: thermostatStatus,
    error: thermostatError,
  } = thermostatsCollection.useFetch(props.thermostatId)

  const {
    data: comfortSettings,
    status: comfortSettingsStatus,
    error: comfortSettingsError,
  } = thermostatComfortSettingsCollection.useFetch(
    `/thermostats/${props.thermostatId}/comfort_settings/`,
  )

  const thermostat = thermostats[0] as Thermostat | undefined
  const thermostatLoading =
    thermostatStatus !== 'succeeded' && !thermostatError && !thermostat
  const comfortSettingsLoading =
    !comfortSettingsError &&
    comfortSettingsStatus !== 'succeeded' &&
    comfortSettings.length === 0

  if (thermostatLoading || comfortSettingsLoading) {
    return <PageLoader />
  }

  return (
    <EventTemperaturesPage
      thermostatId={props.thermostatId}
      comfortSettings={comfortSettings}
      comfortSettingsError={Boolean(comfortSettingsError)}
      isNest={thermostat?.portal === 'Nest'}
    />
  )
}

type PathParams = { deviceId: string | undefined }

export default function EventTemperaturesPageRoot() {
  const { deviceId } = useParams<PathParams>()
  if (!deviceId) return <PageLoader />
  const thermostatId = parseInt(deviceId)
  if (isNaN(thermostatId)) return <PageLoader />
  return <EventTemperaturesPageWithFallback thermostatId={thermostatId} />
}
